/**
 * ReactivateInstanceDialog - Modal de confirmação para REATIVAR uma instância
 * cancelada (provider_instances) do tenant.
 *
 * ARQUITETURA:
 * - A reativação é executada pela RPC `reactivate_cancelled_instance`
 *   (SECURITY DEFINER), que valida ownership, exige cota disponível para o
 *   provider da instância e PRESERVA todas as credenciais/sessão — quando há
 *   inbox vinculada, a conexão volta sem exigir novo QR code.
 * - Sem cota disponível, a RPC retorna erro e a mensagem real é exibida no
 *   toast (exibido pela mutation do useInstances).
 *
 * @version 1.0.0
 * RELEASE: 2026-09-04 v1.0.0 - Versão inicial (layout premium espelhando o
 *                              DeleteInstanceDialog).
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageCircle, RotateCcw, Inbox } from 'lucide-react';
import type { Instance } from '@/hooks/useInstances';

interface ReactivateInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: Instance | null;
  /** Executa a reativação (mutation do hook useInstances) */
  onConfirm: (instance: Instance) => Promise<unknown>;
}

export function ReactivateInstanceDialog({
  open,
  onOpenChange,
  instance,
  onConfirm,
}: ReactivateInstanceDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** Confirma a reativação da instância cancelada */
  const handleConfirm = async () => {
    if (!instance) return;

    setIsSubmitting(true);
    try {
      await onConfirm(instance);
      onOpenChange(false);
    } catch {
      // Toast de erro já é exibido pela mutation (useInstances)
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!instance) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden flex flex-col">
        {/* ===== HEADER PREMIUM (tom de sucesso) ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-green-500/5 via-green-500/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-500/10">
                <RotateCcw className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              </div>
              Reativar instância?
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              A instância volta a consumir uma vaga do seu plano/adicional. Se
              houver inbox vinculada, a conexão atual será mantida — sem novo
              QR code.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO ===== */}
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Card de contexto */}
          <div className="rounded-lg border p-3 flex items-center gap-3 bg-muted/20">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
              <MessageCircle className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground truncate">
                {instance.instance_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {instance.provider_display_name}
              </p>
            </div>
            <Badge
              variant="outline"
              className="font-mono text-[10px] text-muted-foreground border-muted-foreground/30"
            >
              {instance.id.substring(0, 8).toUpperCase()}
            </Badge>
          </div>

          {/* Aviso de inbox vinculada (conexão preservada) */}
          {instance.inbox_id && instance.inbox_name && (
            <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-4 text-sm text-foreground flex items-start gap-3">
              <Inbox className="h-4 w-4 mt-0.5 shrink-0 text-green-600 dark:text-green-400" />
              <p>
                Vinculada à inbox{' '}
                <span className="font-semibold">{instance.inbox_name}</span>. A
                sessão atual será reutilizada — nenhuma reconexão será
                necessária.
              </p>
            </div>
          )}
        </div>

        {/* ===== FOOTER PREMIUM ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Voltar
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-1.5" />
            )}
            Reativar instância
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
